import { useEffect, useState, useCallback, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { Ban, Check, Loader2 } from "lucide-react";

const fmt = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

const MODIFIER_CLASSNAMES = {
  blocked: "line-through text-[#C13515] opacity-70",
  priced: "font-semibold text-[#008489]",
};

export function RoomAvailabilityCalendar({ roomId, roomName, basePrice, open, onOpenChange }) {
  const [range, setRange] = useState(undefined);
  const [avail, setAvail] = useState({});
  const [price, setPrice] = useState("");
  const [rooms, setRooms] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!roomId) return;
    const start = fmt(new Date());
    const end = fmt(new Date(Date.now() + 120 * 86400000));
    try {
      const { data } = await api.get(`/rooms/${roomId}/availability`, { params: { start, end } });
      const map = {};
      data.forEach((d) => { map[d.date] = d; });
      setAvail(map);
    } catch (err) { console.error("availability load failed", err); }
  }, [roomId]);

  useEffect(() => { if (open) { setRange(undefined); setPrice(""); setRooms(""); load(); } }, [open, load]);

  const blockedDays = useMemo(() => Object.values(avail).filter((d) => d.status === "blocked").map((d) => new Date(d.date + "T00:00:00")), [avail]);
  const pricedDays = useMemo(() => Object.values(avail).filter((d) => d.has_override && d.status !== "blocked").map((d) => new Date(d.date + "T00:00:00")), [avail]);
  const disabledDays = useMemo(() => ({ before: new Date() }), []);
  const calendarModifiers = useMemo(() => ({ blocked: blockedDays, priced: pricedDays }), [blockedDays, pricedDays]);
  const selectedInfo = range?.from ? avail[fmt(range.from)] : null;

  const apply = async ({ status, clear }) => {
    if (!range?.from) { toast.error("Select date(s) on the calendar first"); return; }
    const payload = { start: fmt(range.from), end: fmt(range.to || range.from), clear: !!clear };
    if (status) payload.status = status;
    if (!clear && price !== "") payload.price = Number(price);
    if (!clear && rooms !== "") payload.rooms_available = Number(rooms);
    setSaving(true);
    try {
      await api.put(`/host/rooms/${roomId}/availability`, payload);
      toast.success(clear ? "Reset to default" : status === "blocked" ? "Dates blocked" : "Dates updated");
      setRange(undefined); setPrice(""); setRooms("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-lg" data-testid="availability-dialog">
        <DialogHeader><DialogTitle>Availability & pricing — {roomName}</DialogTitle></DialogHeader>
        <DialogDescription className="text-xs text-[#717171] -mt-2">Select dates, then block them or set a custom nightly price & how many rooms are available.</DialogDescription>
        <div className="flex flex-col items-center">
          <Calendar
            mode="range"
            selected={range}
            onSelect={setRange}
            numberOfMonths={1}
            disabled={disabledDays}
            modifiers={calendarModifiers}
            modifiersClassNames={MODIFIER_CLASSNAMES}
          />
          <div className="flex gap-4 text-xs text-[#717171] mb-2">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#C13515]" /> Blocked</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#008489]" /> Custom price</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs mb-1 block">Price / night (₹)</Label><Input data-testid="avail-price-input" type="number" placeholder={`Base ${basePrice}`} value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-xl" /></div>
          <div><Label className="text-xs mb-1 block">Rooms available</Label><Input data-testid="avail-rooms-input" type="number" min={0} placeholder="default" value={rooms} onChange={(e) => setRooms(e.target.value)} className="rounded-xl" /></div>
        </div>
        {selectedInfo && (
          <p className="text-xs text-[#717171]" data-testid="avail-selected-info">
            {selectedInfo.date}: {selectedInfo.status} · {inr(selectedInfo.price)} · {Math.max(0, selectedInfo.rooms_available - selectedInfo.rooms_booked)} of {selectedInfo.rooms_available} left
          </p>
        )}
        <div className="flex flex-wrap gap-2 justify-end pt-1">
          <Button variant="outline" data-testid="avail-clear-btn" disabled={saving} onClick={() => apply({ clear: true })} className="rounded-xl">Reset</Button>
          <Button variant="outline" data-testid="avail-block-btn" disabled={saving} onClick={() => apply({ status: "blocked" })} className="rounded-xl text-[#C13515] border-[#F3C0B3]"><Ban className="w-4 h-4 mr-1" />Block</Button>
          <Button data-testid="avail-save-btn" disabled={saving} onClick={() => apply({ status: "open" })} className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Set / open</>}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
